import { IWasmCustomData, TRoutePopupInfoColorVariant } from "~/interfaces";
import { TEXTURES_CONF, WASM_TEXTURES, WIND_SVG } from "./const";
import { ICustomTexture } from "./interfaces";
import { alignPixel, getImage } from "./utils";
import getWasmTexturesData, { TWasmTextureNames } from "./textures-data";
import { _ } from "~/i18n";
import WasmTextures from "./textures";
import { TPositionShareImageVariant } from "./store/interfaces";

function canvasWhiteClear(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
	ctx.fillStyle = "rgba(255, 255, 255, 0.004)";
	ctx.fillRect(0, 0, canvas.width, canvas.height);
}

/**
 * Ziskani metadat - prepocty, anchor...
 */
function getTextureMetaData(name: TWasmTextureNames, scale: number) {
	let width = WASM_TEXTURES.defSize;
	let height = WASM_TEXTURES.defSize;
	let anchorX = 0;
	let anchorY = 0;
	const data = getWasmTexturesData(name, scale);

	if (data) {
		// retina
		width = alignPixel(data.width / scale);
		height = alignPixel(data.height / scale);
		anchorX = alignPixel(width / 2);
		anchorY = alignPixel(height / 2);

		// pridat vse, co neni kruh - neni to skalovane, vuci 1x texture
		switch (name) {
			// u kapek to proste nesedi kvuli stinu
			case "big-active":
			case "big-inactive":
			case "big-universal-active":
			case "big-universal-inactive":
				anchorX = 24;
				anchorY = height;
				break;

			case "small-active":
			case "small-inactive":
			case "small-universal-active":
			case "small-universal-inactive":
				anchorX = 16;
				anchorY = height;
				break;

			case "small-gray":
				anchorX = 18;
				anchorY = 39;
				break;

			case "little-active":
			case "little-inactive":
				anchorX = 7;
				anchorY = height;
				break;

			case "pano":
				anchorY = height - 8;
				break;

			default:
		}
	}

	return {
		data,
		width,
		height,
		anchorX,
		anchorY,
	};
}

export function getWasmTexture(wasmTextures: WasmTextures, name: TWasmTextureNames, scale: number) {
	const metaData = getTextureMetaData(name, scale);
	// souradnice z textur
	const imageData = metaData.data
		? wasmTextures.ctx.getImageData(metaData.data.x, metaData.data.y, metaData.data.width, metaData.data.height)
		: wasmTextures.ctx.getImageData(0, 0, WASM_TEXTURES.defSize, WASM_TEXTURES.defSize);

	return {
		width: metaData.width,
		height: metaData.height,
		anchorX: metaData.anchorX,
		anchorY: metaData.anchorY,
		imageData,
	};
}

export function getTextMetrics(ctx: CanvasRenderingContext2D, text: string, font: string) {
	ctx.font = font;

	const metrics = ctx.measureText(text);
	const fontSize = parseFloat(font.match(/\d+/u)[0]);

	return {
		width: metrics.width,
		height: Math.max(fontSize, Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent)),
	};
}

/**
 * Rozdel text s fontem do n radku
 */
function splitTextToLines(ctx: CanvasRenderingContext2D, text: string, maxCanvasWidth: number, outlineWidth: number, maxLines?: number) {
	const lines: Array<string> = [];
	const initWidth = ctx.measureText(text).width + outlineWidth;
	let maxWidth = initWidth;

	if (initWidth > maxCanvasWidth) {
		// splitneme na mezery a carky
		const words = text.match(/[^\s-]+|[-]/gu) || [];
		let currentWords = [];

		maxWidth = 0;

		for (const word of words) {
			const currentWord = [...currentWords, word].join(" ");
			const curWidth = ctx.measureText(currentWord).width + outlineWidth;

			if (curWidth > maxCanvasWidth) {
				lines.push(currentWords.join(" "));
				currentWords = [word];
			} else {
				maxWidth = Math.max(maxWidth, curWidth);
				currentWords.push(word);
			}
		}

		if (currentWords.length > 0) {
			lines.push(currentWords.join(" "));
		}
	} else {
		lines.push(text);
	}

	// orezeme
	if (typeof maxLines === "number" && lines.length > maxLines) {
		lines.length = maxLines;
		lines[maxLines - 1] += "...";
	}

	return {
		lines,
		maxWidth,
	};
}

/**
 * Pomocna fce pro kresleni zaoblenych rohu
 */
function drawRoundedRect(ctx: CanvasRenderingContext2D, xPos: number, yPos: number, width: number, height: number, radius: number) {
	ctx.beginPath();
	ctx.moveTo(xPos + radius, yPos);
	ctx.lineTo(xPos + width - radius, yPos);
	ctx.quadraticCurveTo(xPos + width, yPos, xPos + width, yPos + radius);
	ctx.lineTo(xPos + width, yPos + height - radius);
	ctx.quadraticCurveTo(xPos + width, yPos + height, xPos + width - radius, yPos + height);
	ctx.lineTo(xPos + radius, yPos + height);
	ctx.quadraticCurveTo(xPos, yPos + height, xPos, yPos + height - radius);
	ctx.lineTo(xPos, yPos + radius);
	ctx.quadraticCurveTo(xPos, yPos, xPos + radius, yPos);
	ctx.closePath();
}

/**
 * Ziskani kruhu pro vykresleni geo lokace/sdileni polohy
 */
export function getCircle(color: string, width: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const size = width;
	const radiusHalf = alignPixel(size / 2);

	newCanvas.width = size;
	newCanvas.height = size;
	ctx.beginPath();
	ctx.fillStyle = color;
	ctx.arc(radiusHalf, radiusHalf, radiusHalf, 0, Math.PI * 2);
	ctx.fill();

	return {
		imageData: ctx.getImageData(0, 0, size, size),
		canvas: newCanvas,
	};
}

/**
 * Ziskani textu pro search markery
 */
export function getText(text: string, scale: number, width = WASM_TEXTURES.searchMarkersWidth): ICustomTexture & { linesCount: number; } {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const outlineSize = alignPixel(2 * scale);
	// zvetseni sirky - 5 puvodne
	const incWidth = alignPixel(2.5 * scale);
	const maxLines = 2;
	const fontSize = alignPixel(12 * scale);

	// retina
	newCanvas.width = alignPixel(width * scale);

	const maxCanvasWidth = newCanvas.width - incWidth;
	const font = `bold ${fontSize}px arial`;

	ctx.font = font;
	ctx.lineWidth = alignPixel(outlineSize * scale);

	const { lines, maxWidth } = splitTextToLines(ctx, text, maxCanvasWidth, outlineSize, maxLines);

	newCanvas.width = (maxWidth >>> 0) + incWidth;
	newCanvas.height = alignPixel(WASM_TEXTURES.textLineHeight * scale * lines.length);
	canvasWhiteClear(newCanvas, ctx);
	ctx.font = font;

	for (let ind = 0, max = lines.length; ind < max; ind++) {
		const curText = lines[ind];
		const xPos = incWidth / 2;
		const yPos = (ind + 1) * fontSize;

		// tloustka obrysu
		ctx.lineWidth = outlineSize * 2;
		ctx.lineJoin = "round";
		ctx.lineCap = "round";
		// barva obrysu
		ctx.strokeStyle = "white";
		ctx.strokeText(curText, xPos, yPos);
		ctx.fillStyle = "black";
		ctx.fillText(curText, xPos, yPos);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
		linesCount: lines.length,
	};
}

/**
 * Pomocna metoda pro vykresleni zaobleneho obdelniku
 */
function roundedFilledRect(ctx: CanvasRenderingContext2D, rectX: number, rectY: number, rectWidth: number, rectHeight: number, cornerRadius: number, color: string) {
	// Set faux rounded corners
	ctx.lineJoin = "round";
	ctx.lineWidth = cornerRadius;
	ctx.strokeStyle = color;
	ctx.fillStyle = color;

	// Change origin and dimensions to match true size (a stroke makes the shape a bit larger)
	ctx.strokeRect(rectX + (cornerRadius / 2), rectY + (cornerRadius / 2), rectWidth - cornerRadius, rectHeight - cornerRadius);
	ctx.fillRect(rectX + (cornerRadius / 2), rectY + (cornerRadius / 2), rectWidth - cornerRadius, rectHeight - cornerRadius);
}

/**
 * Ziskani hodnoceni pro search markery
 */
export function getReview(value: number, isActive: boolean, scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const width = alignPixel(22 * scale);
	const height = alignPixel(22 * scale);
	const roundedCorner = alignPixel(8 * scale);
	const colors = isActive
		? {
			primary: "#fff",
			secondary: "#1eae00",
		}
		: {
			primary: "#6D6D6D",
			secondary: "#fff",
		};
	const offset = scale;

	newCanvas.width = width;
	newCanvas.height = height;
	canvasWhiteClear(newCanvas, ctx);
	roundedFilledRect(ctx, 0, 0, width, height, roundedCorner, colors.primary);
	roundedFilledRect(ctx, offset, offset, width - (offset * 2), height - (offset * 2), roundedCorner, colors.secondary);

	ctx.font = `bold ${alignPixel(12 * scale)}px arial`;
	ctx.fillStyle = colors.primary;

	const drawText = value.toFixed(1);
	const metrics = getTextMetrics(ctx, drawText, ctx.font);

	ctx.fillText(drawText, (width - metrics.width) / 2, ((height - metrics.height) / 2) + metrics.height - scale);

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Ziskani obrazku pro sdileni polohy
 */
export async function getCircleImage(url: string, size: number, isGray: boolean, scale: number): Promise<ICustomTexture> {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const newSize = alignPixel(size * scale);

	newCanvas.width = newSize;
	newCanvas.height = newSize;

	try {
		const img = await getImage(url, "anonymous");
		const radius = alignPixel(newSize / 2);
		const imgWidth = img.naturalWidth;
		const imgHeight = img.naturalHeight;

		ctx.beginPath();
		ctx.arc(radius, radius, radius, 0, Math.PI * 2, true);
		ctx.closePath();
		ctx.clip();

		if (isGray) {
			ctx.filter = "grayscale(1)";
		}

		// je to mensi jak velikost
		if (imgWidth < size && imgHeight < size) {
			const posX = (newSize - imgWidth) / 2;
			const posY = (newSize - imgHeight) / 2;

			ctx.drawImage(img, 0, 0, imgWidth, imgHeight, posX, posY, imgWidth, imgHeight);
		} else {
			let posX = 0;
			let posY = 0;
			let sourceWidth = imgWidth;
			let sourceHeight = imgHeight;

			// landscape
			if (imgWidth > imgHeight) {
				posX = (imgWidth - imgHeight) / 2;
				sourceWidth = imgHeight;
			// portrait
			} else if (imgHeight > imgWidth) {
				posY = (imgHeight - imgWidth) / 2;
				sourceHeight = imgWidth;
			}

			ctx.drawImage(img, posX, posY, sourceWidth, sourceHeight, 0, 0, newSize, newSize);
		}
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - getCircleImage exception ${exc.message}`);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Batuzek - popisky geometrii
 */
export function getPathCaption(text: string, scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	const maxCanvasWidth = alignPixel(WASM_TEXTURES.pathCaption * scale);
	const shadowLen = alignPixel(2 * scale);
	const fontSize = alignPixel(12 * scale);
	const lineHeight = alignPixel(14 * scale);
	const padding = alignPixel(4 * scale);

	ctx.font = `bold ${fontSize}px arial`;

	const { lines, maxWidth } = splitTextToLines(ctx, text, maxCanvasWidth - (padding * 2), 0, 3);
	const height = (2 * padding) + (lineHeight * lines.length);
	const width = alignPixel(maxWidth + (2 * scale * padding));

	newCanvas.width = width + shadowLen;
	newCanvas.height = height + shadowLen;
	canvasWhiteClear(newCanvas, ctx);
	ctx.shadowColor = "rgba(0, 0, 0, 0.2)";
	ctx.shadowBlur = shadowLen;
	ctx.shadowOffsetX = shadowLen / 2;
	ctx.shadowOffsetY = shadowLen / 2;

	const roundSize = alignPixel(4 * scale);

	// padding 6 8 6
	// 30 vyska jednoho, 54 u 3., line height 14px

	// rectangle color
	ctx.fillStyle = "#ffffff";
	drawRoundedRect(ctx, 1, 1, width, height, roundSize);
	ctx.globalAlpha = 0.8;
	ctx.fill();
	ctx.globalAlpha = 1;

	ctx.shadowColor = "transparent";
	ctx.strokeStyle = "#000000";
	ctx.lineWidth = alignPixel(2 * scale);
	drawRoundedRect(ctx, 1, 1, width, height, roundSize);
	ctx.globalAlpha = 0.56;
	ctx.stroke();

	ctx.globalAlpha = 1;
	ctx.fillStyle = "#000";
	ctx.font = `bold ${fontSize}px arial`;

	let posY = -1;

	for (const line of lines) {
		const metrics = getTextMetrics(ctx, line, ctx.font);

		if (posY === -1) {
			posY = padding + metrics.height;
		}

		ctx.fillText(line, (width - metrics.width) / 2, posY);
		posY += lineHeight;
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

async function renderSvg(svg: string, scale: number, forceFill?: string) {
	let text = svg;

	// doplnime hlavicku
	if (!text.includes("xmlns")) {
		text = text.replace("<svg", `<svg xmlns="http://www.w3.org/2000/svg" `);
	}

	if (forceFill) {
		const fillMatches = text.match(/fill="[^"]+"/ug);

		if (fillMatches) {
			for (const match of fillMatches) {
				text = text.replace(match, `fill="${forceFill}"`);
			}
		}
	}

	const img = await getImage(`data:image/svg+xml;base64,${btoa(text)}`);
	const viewBox = text.match(/viewBox\s*=\s*"([^"]+)/u);
	let width = img.naturalWidth;
	let height = img.naturalHeight;

	// pouzijeme rozmery z view boxu
	if (viewBox) {
		const parts = viewBox[1].split(" ");

		if (parts.length === 4) {
			width = parseFloat(parts[2]);
			height = parseFloat(parts[3]);
		}
	}

	width *= scale;
	height *= scale;

	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	newCanvas.width = width;
	newCanvas.height = height;

	ctx.drawImage(img, 0, 0, width, height);

	return newCanvas;
}

export async function windSvgToCanvas(angle: number, scale: number) {
	const newCanvas = document.createElement("canvas");

	try {
		const svgCanvas = await renderSvg(WIND_SVG, scale);
		const ctx = newCanvas.getContext("2d");
		const data = {
			x: 0,
			y: 0,
			width: svgCanvas.width,
			height: svgCanvas.height,
		};
		const halfWidth = alignPixel(data.width / 2);
		const halfHeight = alignPixel(data.height / 2);

		newCanvas.width = data.width;
		newCanvas.height = data.height;
		ctx.translate(halfWidth, halfHeight);
		ctx.rotate((angle * Math.PI) / 180);
		ctx.drawImage(svgCanvas, -halfWidth, -halfHeight, data.width, data.height);
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - windSvgToCanvas exception ${exc.message}`);
	}

	return newCanvas;
}

/**
 * Fce pro konvertovani svg -> canvas
 */
export async function svgToCanvas(svgUrl: string, scale: number, forceFill?: string): Promise<HTMLCanvasElement | null> {
	try {
		const data = await fetch(svgUrl);

		if (!data.ok) {
			/* eslint-disable-next-line */
			console.error(`Wasm - svgToCanvas request is not ok ${data.status}`);

			return null;
		}

		const text = await data.text();
		const newCanvas = await renderSvg(text, scale, forceFill);

		return newCanvas;
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - svgToCanvas exception ${exc.message}`);

		return null;
	}
}

/**
 * Kolecko pro routovac - start/cil/prujezdni body
 */
export function getRoutePointCircle(radius: number, scale: number, color: string, bgColor = "#ffffff"): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	// puvodne 8
	const size = radius * 2;
	const pos = radius * scale;

	newCanvas.width = alignPixel(size * scale);
	newCanvas.height = alignPixel(size * scale);
	ctx.lineWidth = scale;
	ctx.arc(pos, pos, radius * scale, 0, Math.PI * 2);
	ctx.fillStyle = bgColor;
	ctx.strokeStyle = color;
	ctx.fill();
	ctx.globalAlpha = 0.6;
	ctx.stroke();

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export async function getCenteredIcon(url: string, size: number, iconSize: number, scale: number): Promise<ICustomTexture> {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");

	newCanvas.width = alignPixel(size * scale);
	newCanvas.height = alignPixel(size * scale);
	canvasWhiteClear(newCanvas, ctx);

	try {
		const img = await getImage(url, "anonymous");
		const imgSize = scale * iconSize;
		const top = alignPixel((newCanvas.width - imgSize) / 2);
		const left = alignPixel((newCanvas.height - imgSize) / 2);

		ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, top, left, imgSize, imgSize);
	} catch (exc) {
		/* eslint-disable-next-line */
		console.error(`Wasm - getCenteredIcon exception ${exc.message}`);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export function createRoutePopupContentLines(lines: IWasmCustomData["routePopupInfo"], variant: TRoutePopupInfoColorVariant, scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const fontSizes = [alignPixel(14 * scale), alignPixel(16 * scale)];
	const betweenTexts = alignPixel(5 * scale);
	let width = 0;
	let posY = 0;
	const posYItems = [];

	for (const line of lines) {
		const fontSize = line.bold ? fontSizes[1] : fontSizes[0];
		const measure = getTextMetrics(ctx, line.text, `${line.bold ? "bold " : ""}${fontSize}px arial`);

		width = Math.max(width, measure.width);
		posY += measure.height;
		posYItems.push(posY);
		posY += betweenTexts;
	}

	newCanvas.width = width;
	newCanvas.height = posY;
	canvasWhiteClear(newCanvas, ctx);

	let ind = 0;

	for (const line of lines) {
		const fontSize = line.bold ? fontSizes[1] : fontSizes[0];

		ctx.font = `${line.bold ? "bold " : ""}${fontSize}px arial`;
		ctx.fillStyle = variant === "active"
			? line.activeColor || TEXTURES_CONF.routePopupActiveColor
			: line.inactiveColor || TEXTURES_CONF.routePopupInactiveColor;
		ctx.fillText(line.text, 0, posYItems[ind]);
		ind++;
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/** Textura bez ikonky */
export function getWeatherIconTextureCanvas(hasIcon: boolean, text: string, time: string, scale: number, textBold?: boolean) {
	const newCanvas = document.createElement("canvas");
	const iconSize = alignPixel(24 * scale);
	const padding = alignPixel(5 * scale);
	const ctx = newCanvas.getContext("2d");
	const tempDef = [`${textBold ? "bold " : ""}${alignPixel(14 * scale)}px arial`, "#000000"];
	const timeDef = [`${alignPixel(12 * scale)}px arial`, "#828282"];

	// nelze spocitat, musime zadratovat
	const tempMeas = getTextMetrics(ctx, text, tempDef[0]);
	const timeMeas = getTextMetrics(ctx, time, timeDef[0]);
	const firstTextOffset = hasIcon ? iconSize + padding : 0;
	const textPos = [firstTextOffset, firstTextOffset + tempMeas.width + padding];
	const width = textPos[1] + timeMeas.width;

	newCanvas.width = width;
	newCanvas.height = iconSize;
	canvasWhiteClear(newCanvas, ctx);

	// teplota
	ctx.font = tempDef[0];
	ctx.fillStyle = tempDef[1];
	ctx.fillText(text, textPos[0], newCanvas.height - (tempMeas.height / 2));

	// cas
	ctx.font = timeDef[0];
	ctx.fillStyle = timeDef[1];
	ctx.fillText(time, textPos[1], newCanvas.height - (timeMeas.height / 2) - 2);

	return {
		canvas: newCanvas,
		ctx,
	};
}

/** Cela textura */
export function getWeatherIconTexture(text: string, time: string, textBold: boolean, scale: number, icon?: HTMLCanvasElement): ICustomTexture {
	const { canvas: newCanvas, ctx } = getWeatherIconTextureCanvas(!!icon, text, time, scale, textBold);

	if (icon) {
		// ikonka
		ctx.drawImage(icon, 0, 0);
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

/**
 * Ziskani markeru pro vlastni body
 */
export function getSmallNumber({
	wasmTextures,
	scale = 1,
	value,
	isActive = false,
	opacity = 1,
}: {
	wasmTextures: WasmTextures;
	scale: number;
	value: number;
	isActive?: boolean;
	opacity?: number;
}): ICustomTexture {
	const textureName = isActive ? "small-active" : "small-inactive";
	const data = getTextureMetaData(textureName, scale);
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const width = data.data.width;
	const height = data.data.height;

	newCanvas.width = width;
	newCanvas.height = height;
	canvasWhiteClear(newCanvas, ctx);
	// vykreslime marker
	ctx.globalAlpha = opacity;
	// nemuzu put image data, protoze tam pak nejde nastavit opacity
	ctx.drawImage(wasmTextures.image, data.data.x, data.data.y, width, height, 0, 0, width, height);
	// text - 13px 1x, 26px 2x
	ctx.fillStyle = "black";

	const drawText = value.toString();
	const measure = getTextMetrics(ctx, drawText, `${alignPixel(13 * scale)}px Arial`);
	const offsetLeft = alignPixel((data.anchorX * scale) - (measure.width / 2));
	const offsetTop = alignPixel(((height - measure.height) / 2) + (measure.height / 2));

	ctx.fillText(drawText, offsetLeft, offsetTop - scale);

	return {
		imageData: ctx.getImageData(0, 0, width, height),
		canvas: newCanvas,
	};
}

function calculateFontSize({
	ctx,
	text,
	initFontSize,
	width,
	fontName = "Arial",
	isBold = false,
	fontStep = 1,
	minFontSize = 6,
}: {
	ctx: CanvasRenderingContext2D;
	text: string;
	initFontSize: number;
	width: number;
	fontName: string;
	isBold?: boolean;
	fontStep?: number;
	minFontSize?: number;
}) {
	const getFontStr = (fontSize: number) => `${isBold ? "bold " : ""} ${fontSize}px ${fontName}`;
	let curFontSize = initFontSize;

	ctx.font = getFontStr(curFontSize);

	let metrics = ctx.measureText(text);

	if (metrics.width > width) {
		const maxSteps = Math.floor((initFontSize - minFontSize) / fontStep);

		for (let curStep = maxSteps; curStep >= 0; curStep--) {
			curFontSize -= fontStep;
			ctx.font = getFontStr(curFontSize);
			metrics = ctx.measureText(text);

			if (metrics.width < width) {
				break;
			}
		}
	}

	return curFontSize;
}

/**
 * Ziskani textove ikonky pro routu start/cil
 */
export function getGetRouteStartEnd({
	textWidth,
	wasmTextures,
	scale = 1,
	isEnd = false,
	isActive = false,
	opacity = 1,
}: {
	textWidth: number;
	wasmTextures: WasmTextures;
	scale: number;
	isEnd?: boolean;
	isActive?: boolean;
	opacity?: number;
}): ICustomTexture {
	const textureName = isActive ? "big-active" : "big-inactive";
	const data = getTextureMetaData(textureName, scale);
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const width = data.data.width;
	const height = data.data.height;

	newCanvas.width = width;
	newCanvas.height = height;
	canvasWhiteClear(newCanvas, ctx);
	ctx.globalAlpha = opacity;
	// vykreslime marker
	// nemuzu put image data, protoze tam pak nejde nastavit opacity
	ctx.drawImage(wasmTextures.image, data.data.x, data.data.y, width, height, 0, 0, width, height);
	// text - 11px 1x, 22px 2x
	ctx.fillStyle = "black";

	const value = isEnd
		? _("route.item.end")
		: _("route.item.start");
	const drawText = value.toLocaleUpperCase();
	const fontSize = calculateFontSize({
		ctx,
		fontName: "Arial",
		initFontSize: 11,
		text: drawText,
		width: textWidth,
	});
	const measure = getTextMetrics(ctx, drawText, `${fontSize * scale}px Arial`);
	// musime znovu vynasobit 2x - je to retina
	const offsetLeft = alignPixel((data.anchorX * scale) - (measure.width / 2));
	const offsetTop = alignPixel(((height - measure.height) / 2) + (measure.height / 2));

	ctx.fillText(drawText, offsetLeft, alignPixel(offsetTop - (scale * 4)));

	return {
		imageData: ctx.getImageData(0, 0, width, height),
		canvas: newCanvas,
	};
}

export function getLocationShareDeviceNumber(wasmTextures: WasmTextures, order: number, isActive: boolean, scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const data = getWasmTexturesData(isActive ? "position-share-device-active" : "position-share-device-inactive", scale);
	const ctx = newCanvas.getContext("2d");
	const drawText = order.toString();

	newCanvas.width = data.width;
	newCanvas.height = data.height;
	canvasWhiteClear(newCanvas, ctx);
	// vykreslime marker
	ctx.putImageData(wasmTextures.ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
	// text
	ctx.font = `bold ${alignPixel(14 * scale)}px Arial`;
	ctx.fillStyle = "#5f7182";

	const metrics = getTextMetrics(ctx, drawText, ctx.font);

	ctx.fillText(drawText, alignPixel((data.width / 2) - (metrics.width / 2)), alignPixel(data.height - (2 * metrics.height) - (1 * scale)));

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export async function getLocationShareImage(wasmTextures: WasmTextures, url: string, variant: TPositionShareImageVariant, scale: number): Promise<ICustomTexture> {
	const imageData = await getCircleImage(url, 40, variant === "gray-active" || variant === "gray-inactive", scale);
	const imageCanvas = imageData.canvas;
	const newCanvas = document.createElement("canvas");
	const data = getWasmTexturesData(variant === "active" || variant === "gray-active" ? "position-share-image-active" : "position-share-image-inactive", scale);
	const ctx = newCanvas.getContext("2d");
	const pos = alignPixel((data.width - imageCanvas.width) / 2);

	newCanvas.width = data.width;
	newCanvas.height = data.height;
	canvasWhiteClear(newCanvas, ctx);
	// kolecko
	ctx.putImageData(wasmTextures.ctx.getImageData(data.x, data.y, data.width, data.height), 0, 0);
	// fotka
	ctx.drawImage(imageCanvas, 0, 0, imageCanvas.width, imageCanvas.height, pos, pos - scale, imageCanvas.width, imageCanvas.height);

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export function createPubtMidpoint(size: number, drawText: string, scale: number) {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const radiusHalf = alignPixel(size / 2);
	const borderWidth = alignPixel(2 * scale);

	newCanvas.width = size;
	newCanvas.height = size;
	canvasWhiteClear(newCanvas, ctx);
	ctx.beginPath();
	ctx.fillStyle = "#e1e4e7";
	ctx.arc(radiusHalf, radiusHalf, radiusHalf, 0, Math.PI * 2);
	ctx.fill();
	ctx.beginPath();
	ctx.fillStyle = "#fff";
	ctx.arc(radiusHalf, radiusHalf, radiusHalf - borderWidth, 0, Math.PI * 2);
	ctx.fill();
	ctx.fillStyle = "#677888";

	const font = `bold ${12 * scale}px arial`;
	const textMeasure = getTextMetrics(ctx, drawText, font);

	ctx.font = font;
	ctx.fillText(drawText, alignPixel((size - textMeasure.width) / 2), alignPixel(((size - textMeasure.height) / 2) + textMeasure.height - (2 * scale)));

	return newCanvas;
}

/** Max 5 ikonek na radku */
export function createRoutePopupPubtSummary(wasmTextures: WasmTextures, pubtData: IWasmCustomData["routePopupPubtSummary"], scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const padding = alignPixel(4 * scale);
	const fontSize = alignPixel(16 * scale);
	const font = `${fontSize}px arial`;
	let width = 0;
	let height = 0;
	const perLine = 5;
	const iconsLen = pubtData.icons.length;
	const lines = Math.ceil(iconsLen / perLine);
	const iconSize = alignPixel(24 * scale);
	const iconsWidth = alignPixel((iconSize * Math.min(perLine, iconsLen)) + ((Math.min(iconsLen, perLine) - 1) * padding));
	const warning = getWasmTexturesData("warning", scale);

	ctx.fillStyle = "#000000";

	const textMeasure = getTextMetrics(ctx, pubtData.text, font);

	width = textMeasure.width;

	if (pubtData.warning) {
		width += padding + warning.width;
	}

	height = textMeasure.height + (iconsLen > 0 ? padding : 0) + ((lines * iconSize) + (lines * padding));

	newCanvas.width = Math.max(width, iconsWidth);
	newCanvas.height = height;
	canvasWhiteClear(newCanvas, ctx);

	ctx.fillStyle = "#000000";
	ctx.font = font;
	ctx.fillText(pubtData.text, 0, textMeasure.height);

	if (pubtData.warning) {
		ctx.drawImage(wasmTextures.image, warning.x, warning.y, warning.width, warning.height, textMeasure.width + padding, ((fontSize - warning.height) / 2) + padding, warning.width, warning.height);
	}

	let iconPosY = alignPixel(textMeasure.height + (padding * 2));

	for (let ind = 0, max = iconsLen; ind < max; ind++) {
		const curPosX = ind % perLine;

		if (ind > 0 && curPosX === 0) {
			iconPosY += padding + iconSize;
		}

		const iconPosX = curPosX > 0
			? (iconSize * curPosX) + (curPosX * padding)
			: 0;

		if (pubtData.icons[ind].icon) {
			const data = getWasmTexturesData(pubtData.icons[ind].icon, scale);

			ctx.drawImage(wasmTextures.image, data.x, data.y, data.width, data.height, iconPosX, iconPosY, data.width, data.height);
		} else if (pubtData.icons[ind].midpoint) {
			const midPoint = createPubtMidpoint(iconSize, pubtData.icons[ind].midpoint, scale);

			ctx.drawImage(midPoint, iconPosX, iconPosY);
		}
	}

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export function createRoutePopupPubtSection(wasmTextures: WasmTextures, pubtData: IWasmCustomData["routePopupPubtSection"], scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const iconSize = alignPixel(20 * scale);
	const data = getWasmTexturesData("mhd-map-bus", scale);
	const font = `${alignPixel(12 * scale)}px arial`;

	newCanvas.width = alignPixel(iconSize * 3);
	newCanvas.height = iconSize;
	canvasWhiteClear(newCanvas, ctx);
	ctx.drawImage(wasmTextures.image, data.x, data.y, data.width, data.height, 0, 0, iconSize, iconSize);
	ctx.font = font;
	ctx.fillStyle = "#f00";
	ctx.fillText(pubtData.desc, iconSize, iconSize);

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}

export function createRoutePopupClosure(wasmTextures: WasmTextures, closureData: IWasmCustomData["routePopupClosure"], scale: number): ICustomTexture {
	const newCanvas = document.createElement("canvas");
	const ctx = newCanvas.getContext("2d");
	const iconSize = alignPixel(20 * scale);
	const data = getWasmTexturesData(closureData.icon, scale);
	const padding = alignPixel(4 * scale);
	const font = `${alignPixel(12 * scale)}px arial`;
	// ikonka je 20
	let width = iconSize;
	const textMeasure = getTextMetrics(ctx, closureData.text, font);

	width += padding + textMeasure.width + padding;
	newCanvas.width = width;
	newCanvas.height = iconSize;
	canvasWhiteClear(newCanvas, ctx);
	ctx.drawImage(wasmTextures.image, data.x, data.y, data.width, data.height, 0, 0, iconSize, iconSize);
	ctx.fillStyle = "#c00";
	ctx.font = font;
	ctx.fillText(closureData.text, iconSize + padding, alignPixel(((iconSize - textMeasure.height) / 2) + textMeasure.height - (2 * scale)));

	return {
		imageData: ctx.getImageData(0, 0, newCanvas.width, newCanvas.height),
		canvas: newCanvas,
	};
}
